import {computed, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {useAuth} from '#imports';
import {readonlyState} from '@malt/utils';
import {Configuration, ExpertiseApi, PublicExpertiseApi, type Skill} from '@api';
import {expertiseApiMapper} from '~/features/expertises/helpers/ExpertiseApiMapper';
import {useProfileStore} from '../profileStore';

export interface ProfileSkillSet {
    selectedSkills: Skill[];
    topSkills: Skill[];
}

export const useSkillSetStore = defineStore('skill-set', () => {
    const apiConfiguration = new Configuration({basePath: ''});
    const expertiseApi = new ExpertiseApi(apiConfiguration);
    const publicExpertiseApi = new PublicExpertiseApi(apiConfiguration);

    const {profile} = storeToRefs(useProfileStore());

    const {loggedIn} = storeToRefs(useAuth());

    const profileSkillSet = ref<ProfileSkillSet>();
    const profileTopSkills = computed(() => profileSkillSet.value?.topSkills ?? []);
    const profileSelectedSkills = computed(() => profileSkillSet.value?.selectedSkills ?? []);
    const profileSkills = computed(() => [...profileTopSkills.value, ...profileSelectedSkills.value]);

    async function fetchSkillSet(): Promise<void> {
        if (!loggedIn.value) {
            profileSkillSet.value = await publicExpertiseApi.getPublicSkillsFromProfileId({profileId: profile.value!.profileId});
        } else if (profile.value?.profileId) {
            profileSkillSet.value = await expertiseApi.getSkillsFromProfileId({profileId: profile.value.profileId});
        } else {
            profileSkillSet.value = await expertiseApi.getSkills();
        }
    }

    async function saveSkillSet(newSelectedSkills: Skill[], newTopSkills: Skill[]): Promise<void> {
        await expertiseApi.updateSkills(expertiseApiMapper.toUpdateSkillsRequestApi(newSelectedSkills, newTopSkills));
        await fetchSkillSet();
    }

    function setProfileSkillSet(newSelectedSkills: Skill[] | undefined, newTopSkills: Skill[] | undefined): void {
        profileSkillSet.value = {
            selectedSkills: newSelectedSkills ?? [],
            topSkills: newTopSkills ?? [],
        };
    }

    return {
        profileSkillSet: readonlyState(profileSkillSet),
        profileTopSkills,
        profileSelectedSkills,
        profileSkills,
        setProfileSkillSet,
        fetchSkillSet,
        saveSkillSet,
    };
});
